import { createSelector } from '@reduxjs/toolkit';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';

import { organizationModel } from '@/entities/organization';
import { studyModel } from '@/entities/study';

import { RootState } from '@/app/model/store';

const selectIsForbidUploadsFromWeb = (state: RootState) =>
  organizationModel.selectors.selectFeatureFlag(
    FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
  )(state);

const selectHasCBCTStudies = createSelector(
  (state: RootState) =>
    studyModel.selectors.selectByType(StudyType.StudyType_CBCT)(state),
  (studies) => studies.length > 0,
);

const selectHasMultipleCBCTStudies = createSelector(
  (state: RootState) =>
    studyModel.selectors.selectByType(StudyType.StudyType_CBCT)(state),
  (studies) => studies.length > 1,
);

const selectHasPanoStudies = createSelector(
  (state: RootState) =>
    studyModel.selectors.selectByType(StudyType.StudyType_PanoramicXRay)(state),
  (studies) => studies.length > 0,
);

const selectHasIOXRayStudies = createSelector(
  (state: RootState) =>
    studyModel.selectors.selectByType(StudyType.StudyType_IntraoralXRay)(state),
  (studies) => studies.length > 0,
);

const selectHasIOSMeshesStudies = createSelector(
  (state: RootState) =>
    studyModel.selectors.selectByType(StudyType.StudyType_IOS_Meshes)(state),
  (studies) => studies.length > 0,
);

export const selectCanShowCBCTButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasCBCTStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShowPanoButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasPanoStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShowIOXRayButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasIOXRayStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShow3DStudioButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasCBCTStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShowOrthoStudioButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasCBCTStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShowIOSSegmentationButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasIOSMeshesStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectCanShowRawMeshViewerButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasIOSMeshesStudies],
  (isForbidden, hasStudies) => !isForbidden || hasStudies,
);

export const selectIsIOSBlockedByUploadRestriction = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasIOSMeshesStudies],
  (isForbidden, hasIOS) => isForbidden && !hasIOS,
);

export const selectIsCBCTAlignmentBlockedByUploadRestriction = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasMultipleCBCTStudies],
  (isForbidden, hasMultipleCBCT) => isForbidden && !hasMultipleCBCT,
);

export const selectCanShowImplantStudioButton = createSelector(
  [selectIsForbidUploadsFromWeb, selectHasCBCTStudies],
  (isForbidden, hasCBCT) => !isForbidden || hasCBCT,
);

export const selectCanShowDentalPhotoButton = createSelector(
  [selectIsForbidUploadsFromWeb],
  (isForbidden) => !isForbidden,
);

export const selectOrderReportButtonsVisibility = createSelector(
  [
    selectCanShowCBCTButton,
    selectCanShowPanoButton,
    selectCanShowIOXRayButton,
    selectCanShow3DStudioButton,
    selectCanShowOrthoStudioButton,
    selectCanShowIOSSegmentationButton,
    selectCanShowRawMeshViewerButton,
    selectCanShowImplantStudioButton,
    selectCanShowDentalPhotoButton,
    organizationModel.selectors.selectFDAEnvironment,
  ],
  (
    cbct,
    pano,
    ioxray,
    studio3d,
    ortho,
    iosSegmentation,
    rawMeshViewer,
    implant,
    dentalPhoto,
    fdaEnvironment,
  ) => ({
    cbct,
    pano,
    ioxray,
    studio3d: fdaEnvironment.isFDAVersion2 ? false : studio3d,
    ortho: fdaEnvironment.isFDAVersion2 ? false : ortho,
    iosSegmentation: fdaEnvironment.isFDAVersion2 ? false : iosSegmentation,
    rawMeshViewer: fdaEnvironment.isFDAVersion2 ? false : rawMeshViewer,
    implant: fdaEnvironment.isFDAVersion2 ? false : implant,
    dentalPhoto: fdaEnvironment.isFDAVersion2 ? false : dentalPhoto,
  }),
);

export const selectHasAnyVisibleOrderReportButton = createSelector(
  [selectOrderReportButtonsVisibility],
  (visibility) => Object.values(visibility).some((isVisible) => isVisible),
);
